// Turns member-written text into text + link segments for LinkifiedText.
//
// Deliberately BARE URLS ONLY — no Markdown [text](url), no HTML, no BBCode.
// Testers tried all three (see the "Can someone just post?" thread). A link
// whose visible text differs from its destination is the classic phishing
// shape, and this is a community of support staff who share links to
// resources with each other; showing the real address is the safer default.
// If labelled links are ever wanted, show the hostname beside the label.
//
// Nothing here produces HTML. Segments are rendered by React as text and <a>
// elements, so a pasted `<a href="javascript:…">` stays literal text, and every
// href is re-parsed with URL() and must be http(s).

// http(s)://… or www.… up to whitespace or a character that can't appear
// unescaped in a URL someone pasted into prose. Square brackets end a link:
// browsers percent-encode them when copying, and allowing them turned a
// BBCode attempt — [url=https://x]https://x[/url] — into one garbled link.
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"'`[\]]+/gi;

// Trailing punctuation that belongs to the sentence, not the link:
// "see https://nadsp.org." / "(https://nadsp.org)".
const TRAILING_RE = /[.,;:!?'"*_~]+$/;

const trimTrailing = (raw) => {
  let url = raw.replace(TRAILING_RE, '');
  // Drop unbalanced closing brackets, keeping balanced ones such as
  // https://en.wikipedia.org/wiki/Foo_(bar).
  for (const [open, close] of [['(', ')'], ['{', '}']]) {
    while (url.endsWith(close) && url.split(close).length > url.split(open).length) {
      url = url.slice(0, -1).replace(TRAILING_RE, '');
    }
  }
  return url;
};

export const toSafeHref = (candidate) => {
  try {
    const href = /^www\./i.test(candidate) ? `https://${candidate}` : candidate;
    const url = new URL(href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (!url.hostname.includes('.')) return null;
    return url.href;
  } catch {
    return null;
  }
};

export const linkify = (text) => {
  if (!text) return [];
  const segments = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    const display = trimTrailing(match[0]);
    const href = toSafeHref(display);
    if (!href) continue;
    if (match.index > last) segments.push({ type: 'text', value: text.slice(last, match.index) });
    segments.push({ type: 'link', value: display, href });
    last = match.index + display.length;
  }
  if (last < text.length) segments.push({ type: 'text', value: text.slice(last) });
  return segments;
};
